import React from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Plus, X as XIcon, Search, Building2, Shield, ChevronDown, StickyNote, AlertTriangle, Sparkles, SlidersHorizontal } from 'lucide-react';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import { ApiEndPoint } from '@/services/apiUttiLities';
import { cleanRoles as cleanScenarioRoles, getRoleInstructions } from './ScenarioRoleField';
import { expandDistributionRecipients } from '@/uitilites/utility';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import socketService from '@/services/socketService';
import {
  normalizeScenarioCandidateSources,
  scenarioCandidateSourceSearchText,
  scenarioCandidateVia,
} from './ScenarioCandidateSourceChips';
import { mergeIncidentReadinessMap } from './liveIncidentReadiness';
import {
  DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
  GPS_STATUS_FILTERS,
  LAST_SEEN_WITHIN_FILTERS,
  MAX_DISTANCE_FILTERS,
  MAX_ETA_FILTERS,
  dutyStatusMeta,
  dutyRank,
  mobilePresenceMeta,
  passesPersonnelAvailabilityFilters,
  personnelAssignmentWarnings,
  readinessLocation,
  readinessRank,
  readinessStatusMeta,
  usableReadinessDistance,
} from './personnelAvailability';

// ─────────────────────────────────────────────────────────────────────────────
// Personnel assignment modal — mounted ONCE per incident page, OUTSIDE the
// timeline card tree. Card panels only dispatch 'openAssignPersonnel'; because
// this component never remounts with the cards, the modal is immune to the
// background refetches that used to rebuild (and visually destabilise) it.
// ─────────────────────────────────────────────────────────────────────────────

// ---- shared data caches (users / departments / teams / duty) ----
// Short TTL: opening the modal refetches anything older than a few seconds, so
// members added in Departments & Units (or a fresh duty check) show up without
// a page reload — a permanent cache made new members "appear after a while".
const CACHE_TTL_MS = 10000;
let _usersPromise = null; let _usersAt = 0;
export const loadAssignUsers = () => {
  if (!_usersPromise || Date.now() - _usersAt > CACHE_TTL_MS) {
    _usersAt = Date.now();
    _usersPromise = CommanServices.getAppUsers().catch(() => ({ data: [] })).then((uRes) => {
      const raw = Array.isArray(uRes?.data) ? uRes.data : [];
      return {
        raw,
        display: raw.map((u) => ({
          id: u.id || u._id,
          name: u.name || u.full_name || u.email || 'User',
          position: u.position || u.access_level || '',
        })),
      };
    });
  }
  return _usersPromise;
};
let _deptsPromise = null; let _deptsAt = 0;
export const loadAssignDepartments = () => {
  if (!_deptsPromise || Date.now() - _deptsAt > CACHE_TTL_MS) {
    _deptsAt = Date.now();
    _deptsPromise = CommanServices.departments().catch(() => ({ data: [] })).then((r) =>
      (Array.isArray(r?.data) ? r.data : []).filter((d) => d && d.id)
    );
  }
  return _deptsPromise;
};
let _teamsPromise = null; let _teamsAt = 0;
export const loadAssignTeams = () => {
  if (!_teamsPromise || Date.now() - _teamsAt > CACHE_TTL_MS) {
    _teamsAt = Date.now();
    _teamsPromise = CommanServices.getTeamsData().catch(() => ({ data: [] })).then((r) =>
      Array.isArray(r?.data) ? r.data : []
    );
  }
  return _teamsPromise;
};
let _dutyPromise = null; let _dutyAt = 0;
export const loadAssignDuty = () => {
  if (!_dutyPromise || Date.now() - _dutyAt > CACHE_TTL_MS) {
    _dutyAt = Date.now();
    _dutyPromise = axios
      .get(`${ApiEndPoint}/api/duty-status`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      .then((r) => r.data?.data?.users || {})
      .catch(() => ({}));
  }
  return _dutyPromise;
};

const SMART_PRESET_FILTERS = Object.freeze({
  seenWithin: 60,
  gps: 'usable',
  maxDistance: 5000,
  maxEta: 30,
});

const PERSONNEL_FILTER_PRESETS = [
  { id: 'smart', label: 'Smart', filters: SMART_PRESET_FILTERS, smart: true },
  { id: 'ready', label: 'Ready now', filters: { incident: 'in_duty' } },
  { id: 'duty', label: 'On duty', filters: { duty: 'in_duty' } },
  { id: 'online', label: 'Online', filters: { app: 'online' } },
  { id: 'nearby', label: 'Nearby ≤ 5km', filters: { maxDistance: 5000 } },
];

const ADVANCED_FILTER_GROUPS = [
  { label: 'Seen', field: 'seenWithin', options: LAST_SEEN_WITHIN_FILTERS },
  { label: 'GPS', field: 'gps', options: GPS_STATUS_FILTERS },
  { label: 'Distance', field: 'maxDistance', options: MAX_DISTANCE_FILTERS },
  { label: 'ETA', field: 'maxEta', options: MAX_ETA_FILTERS },
];

const keepPreviousWhenEqual = (previous, next) => (
  JSON.stringify(previous) === JSON.stringify(next) ? previous : next
);

// ---- shared persistence (used by the modal AND the card panels' chip removal) ----
// Normal edits belong to the card where the administrator made them. Explicit
// bulk actions in the modal can copy that staffing to matching cards. Saves
// queue sequentially so an earlier PATCH can never land after (and silently
// undo) a later one; _lastSent keeps each card's latest payload so queued saves
// never rebuild from stale item props.
let _persistQueue = Promise.resolve();
let _pendingSaves = 0;
const _lastSent = {}; // itemId -> last roleAssignments object we PATCHed
export const PERSONNEL_ASSIGNMENTS_OPTIMISTIC_EVENT = 'personnelAssignmentsOptimistic';

const latestRoleAssignments = (item) => _lastSent[item?.id] || item?.meta?.roleAssignments || {};

const isActiveAssignmentCard = (item) => !!(
  item?.id
  && item.is_active !== false
  && !item.isVirtual
  && !item?.meta?.isVirtual
  && item.group !== 'report'
  && !item?.meta?.superseded
  && !item?.meta?.isSuperseded
  && !item?.meta?.suppressed
);

const SCENARIO_AUTO_ASSIGNMENT_FIELDS = [
  'fromScenarioDefault',
  'from_scenario_default',
  'autoAssignedFromScenario',
  'auto_assigned_from_scenario',
  'assignmentSource',
  'assignment_source',
  'autoAssignmentSource',
  'auto_assignment_source',
  'autoAssignedAt',
  'auto_assigned_at',
];

const withoutScenarioAutoFields = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const cleaned = { ...value };
  SCENARIO_AUTO_ASSIGNMENT_FIELDS.forEach((field) => { delete cleaned[field]; });
  if (cleaned.meta && typeof cleaned.meta === 'object' && !Array.isArray(cleaned.meta)) {
    cleaned.meta = withoutScenarioAutoFields(cleaned.meta);
  }
  if (cleaned.metadata && typeof cleaned.metadata === 'object' && !Array.isArray(cleaned.metadata)) {
    cleaned.metadata = withoutScenarioAutoFields(cleaned.metadata);
  }
  return cleaned;
};

export const asManualAssignment = (entry) => withoutScenarioAutoFields(entry);

const uniqueManualAssignments = (entries) => {
  const seen = new Set();
  return (entries || []).flatMap((entry) => {
    if (!entry?.id || seen.has(String(entry.id))) return [];
    seen.add(String(entry.id));
    return [asManualAssignment(entry)];
  });
};

const uniqueValidAssignmentCount = (entries) => new Set(
  (entries || []).filter((entry) => entry?.id).map((entry) => String(entry.id))
).size;

const persistAssignmentUpdates = (incident, updates, { onSettled } = {}) => {
  const validUpdates = (updates || []).filter((update) => update?.id && update?.roleAssignments);
  if (!incident?.id || validUpdates.length === 0) {
    onSettled?.({ succeeded: false, skipped: true });
    return Promise.resolve(false);
  }

  validUpdates.forEach(({ id, roleAssignments }) => { _lastSent[id] = roleAssignments; });

  window.dispatchEvent(new CustomEvent(PERSONNEL_ASSIGNMENTS_OPTIMISTIC_EVENT, {
    detail: { incident_id: incident.id, items: validUpdates },
  }));

  _pendingSaves += 1;
  const operation = _persistQueue.then(async () => {
    let succeeded = false;
    try {
      const results = await Promise.all(validUpdates.map(({ id, roleAssignments }) =>
        CommanServices.updateIncidentTimeline(incident.id, id, { meta: { roleAssignments } })
      ));
      succeeded = results.every((result) => result?.status !== false);
      if (!succeeded) throw new Error('Unable to save one or more personnel assignments');
      return true;
    } catch (error) {
      console.error('Personnel assignment save failed:', error);
      return false;
    } finally {
      _pendingSaves -= 1;
      if (_pendingSaves === 0) {
        // Reconcile once after the entire burst, whether it succeeded or needs
        // to roll back to server truth. Never clear the visible card mid-pick.
        window.dispatchEvent(new Event('timelineRefresh'));
        setTimeout(() => { if (_pendingSaves === 0) Object.keys(_lastSent).forEach((k) => delete _lastSent[k]); }, 1500);
      }
      onSettled?.({ succeeded });
    }
  });
  // Keep the shared queue usable even when a request fails.
  _persistQueue = operation.then(() => undefined, () => undefined);
  return operation;
};

export const persistRoleChanges = (incident, sourceItem, _allItems, changes, { onSettled } = {}) => {
  if (!incident?.id || !sourceItem?.id) {
    onSettled?.({ succeeded: false, skipped: true });
    return Promise.resolve(false);
  }

  // Publish the source card's complete optimistic snapshot immediately.
  // Sibling cards change only through the explicit bulk actions below.
  const roleAssignments = { ...latestRoleAssignments(sourceItem), ...changes };
  return persistAssignmentUpdates(incident, [{ id: sourceItem.id, roleAssignments }], { onSettled });
};

export const hasPendingAssignSaves = () => _pendingSaves > 0;

// Card panels call this to summon the modal.
export const openAssignPersonnel = (detail) => {
  window.dispatchEvent(new CustomEvent('openAssignPersonnel', { detail }));
};

export const findCardRoleStaffingReview = (review, itemId, roleName) => {
  const card = (review?.cards || []).find((entry) => String(entry?.id) === String(itemId));
  return (card?.roles || []).find((entry) => String(entry?.role) === String(roleName)) || null;
};

export const smartRecommendationCandidates = (review, itemId, roleName, ranking = {}) => {
  const role = findCardRoleStaffingReview(review, itemId, roleName);
  if (!role) return [];
  const dutyInfoFor = typeof ranking?.dutyInfoFor === 'function' ? ranking.dutyInfoFor : () => ({});
  const readinessInfoFor = typeof ranking?.readinessInfoFor === 'function' ? ranking.readinessInfoFor : () => null;
  const readinessState = ranking?.readinessLoadState || 'loaded';
  const recommendedOrder = new Map(
    (role.recommended_user_ids || []).map((userId, index) => [String(userId), index])
  );
  return (role.candidates || []).map((candidate, index) => ({
    ...candidate,
    is_recommended: recommendedOrder.has(String(candidate?.user_id)),
    recommendation_order: recommendedOrder.get(String(candidate?.user_id)) ?? Number.MAX_SAFE_INTEGER,
    candidate_order: index,
    saved_order: recommendedOrder.has(String(candidate?.user_id))
      ? recommendedOrder.get(String(candidate?.user_id))
      : recommendedOrder.size + index,
  })).sort((left, right) => (
    dutyRank(dutyInfoFor(left.user_id)) - dutyRank(dutyInfoFor(right.user_id))
    || readinessRank(readinessInfoFor(left.user_id), readinessState) - readinessRank(readinessInfoFor(right.user_id), readinessState)
    || usableReadinessDistance(readinessInfoFor(left.user_id)) - usableReadinessDistance(readinessInfoFor(right.user_id))
    || left.saved_order - right.saved_order
    || left.candidate_order - right.candidate_order
  ));
};

export default function AssignPersonnelModal({ incident, allItems = [], onChanged, onlineUserIds = new Set(), presenceByUserId = {}, isAdmin = true }) {
  const [open, setOpen] = React.useState(null); // { itemId, role, variant, itemFallback }
  const [users, setUsers] = React.useState([]);
  const [rawUsers, setRawUsers] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [teams, setTeams] = React.useState([]);
  const [duty, setDuty] = React.useState({});
  const [incidentReadiness, setIncidentReadiness] = React.useState({});
  const [readinessLoadState, setReadinessLoadState] = React.useState('loaded');
  const [staffingReview, setStaffingReview] = React.useState({ cards: [] });
  const [staffingReviewLoadState, setStaffingReviewLoadState] = React.useState('loaded');
  const [query, setQuery] = React.useState('');
  const [tab, setTab] = React.useState('people');
  const [availabilityFilters, setAvailabilityFilters] = React.useState(() => ({ ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS }));
  const [activePreset, setActivePreset] = React.useState(null);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = React.useState(false);
  const [groupNotice, setGroupNotice] = React.useState(null); // { key, msg } — "pick only N" hint
  const [expandedGroups, setExpandedGroups] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [bulkNotice, setBulkNotice] = React.useState('');
  const [confirmReplaceAll, setConfirmReplaceAll] = React.useState(false);
  const [assignmentConfirmation, setAssignmentConfirmation] = React.useState(null);
  const [assignments, setAssignments] = React.useState({});
  const pendingRef = React.useRef(0);
  const assignmentsRef = React.useRef(assignments);
  const staffingReviewRef = React.useRef(staffingReview);
  const locationRefreshTimerRef = React.useRef(null);
  const modalRef = React.useRef(null);
  const confirmationCancelRef = React.useRef(null);
  const confirmationConfirmRef = React.useRef(null);
  const confirmationReturnFocusRef = React.useRef(null);
  const confirmationWasOpenRef = React.useRef(false);
  const modalTitleId = React.useId();
  const confirmationTitleId = React.useId();
  const confirmationMessageId = React.useId();
  const confirmationWarningsId = React.useId();
  React.useEffect(() => { assignmentsRef.current = assignments; }, [assignments]);
  React.useEffect(() => { staffingReviewRef.current = staffingReview; }, [staffingReview]);

  React.useEffect(() => {
    if (assignmentConfirmation) {
      if (!confirmationWasOpenRef.current) {
        confirmationReturnFocusRef.current = document.activeElement;
      }
      confirmationWasOpenRef.current = true;
      confirmationCancelRef.current?.focus();
      return;
    }
    if (!confirmationWasOpenRef.current) return;
    confirmationWasOpenRef.current = false;
    const returnTarget = confirmationReturnFocusRef.current;
    confirmationReturnFocusRef.current = null;
    if (returnTarget?.isConnected && typeof returnTarget.focus === 'function') returnTarget.focus();
    else modalRef.current?.focus();
  }, [assignmentConfirmation]);

  const refreshIncidentReadiness = React.useCallback(async ({ background = false } = {}) => {
    if (!incident?.id) {
      setIncidentReadiness({});
      setReadinessLoadState('loaded');
      return;
    }
    if (!background) setReadinessLoadState('loading');
    try {
      const latest = await CommanServices.getIncidentReadinessCheck(incident.id);
      if (latest?.status === false) throw new Error(latest?.message || 'Unable to load readiness');
      const checkId = latest?.data?.id;
      if (!checkId) {
        setIncidentReadiness({});
        setReadinessLoadState('loaded');
        return;
      }
      const response = await CommanServices.getDutyCheckResponses(checkId);
      if (response?.status === false) throw new Error(response?.message || 'Unable to load readiness responses');
      const next = {};
      (response?.data?.rows || []).forEach((row) => {
        if (row?.user_id) next[String(row.user_id)] = row;
      });
      setIncidentReadiness((previous) => keepPreviousWhenEqual(previous, next));
      setReadinessLoadState('loaded');
    } catch (_error) {
      if (!background) {
        setIncidentReadiness({});
        setReadinessLoadState('unavailable');
      }
    }
  }, [incident?.id]);

  const refreshStaffingReview = React.useCallback(async ({ background = false } = {}) => {
    if (!incident?.id) {
      setStaffingReview({ cards: [] });
      setStaffingReviewLoadState('loaded');
      return;
    }
    if (!background) setStaffingReviewLoadState('loading');
    try {
      const response = await CommanServices.getIncidentStaffingReview(incident.id);
      if (response?.status === false) throw new Error(response?.message || 'Unable to load recommendations');
      const next = response?.data || { cards: [] };
      setStaffingReview((previous) => keepPreviousWhenEqual(previous, next));
      setStaffingReviewLoadState('loaded');
    } catch (_error) {
      if (!background) {
        setStaffingReview({ cards: [] });
        setStaffingReviewLoadState('unavailable');
      }
    }
  }, [incident?.id]);

  React.useEffect(() => {
    const h = (e) => {
      const d = e.detail || {};
      if (!d.itemId || !d.role) return;
      const initialAssignments = d.item?.meta?.roleAssignments || {};
      assignmentsRef.current = initialAssignments;
      setAssignments(initialAssignments);
      setOpen({
        itemId: d.itemId,
        role: d.role,
        variant: d.variant || 'light',
        itemFallback: d.item || null,
        replaceUserId: d.replaceUserId ? String(d.replaceUserId) : null,
        replaceUserName: d.replaceUserName || '',
      });
      setQuery('');
      const openedRole = (d.item?.meta?.scenarioRoles || []).find((role) => String(role?.role) === String(d.role));
      const hasSavedCandidates = [
        ...(openedRole?.staffing?.preferred || []),
        ...(openedRole?.staffing?.fallbacks || []),
      ].length > 0;
      setTab(hasSavedCandidates ? 'recommended' : 'people');
      setAvailabilityFilters({ ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS });
      setActivePreset(null);
      setAdvancedFiltersOpen(false);
      setExpandedGroups({});
      setGroupNotice(null);
      setSaveError('');
      setBulkNotice('');
      setConfirmReplaceAll(false);
      setAssignmentConfirmation(null);
      if (!isAdmin && incident?.id) {
        CommanServices.getIncidentRecipientDirectory(incident.id)
          .then(normalizeIncidentRecipientDirectory)
          .then((directory) => {
            setRawUsers(directory.users);
            setUsers(directory.users.map((user) => ({
              id: user.id || user._id,
              name: user.name || user.full_name || user.email || 'User',
              position: user.position || user.access_level || '',
            })));
            setDepartments(directory.departments);
            setTeams(directory.teams);
            setDuty(directory.duty);
          })
          .catch(() => {
            setRawUsers([]);
            setUsers([]);
            setDepartments([]);
            setTeams([]);
            setDuty({});
          });
      } else {
        loadAssignUsers().then(({ raw, display }) => { setRawUsers(raw); setUsers(display); });
        loadAssignDepartments().then(setDepartments);
        loadAssignTeams().then(setTeams);
        loadAssignDuty().then(setDuty);
      }
      setIncidentReadiness({});
      setStaffingReview({ cards: [] });
      refreshIncidentReadiness();
      refreshStaffingReview();
    };
    window.addEventListener('openAssignPersonnel', h);
    return () => window.removeEventListener('openAssignPersonnel', h);
  }, [incident?.id, isAdmin, refreshIncidentReadiness, refreshStaffingReview]);

  React.useEffect(() => {
    if (!open || !incident?.id) return undefined;
    const unsubscribeReadiness = socketService.on('staffing_readiness_updated', (event) => {
      if (String(event?.incident_id) === String(incident.id)) {
        if (event?.user_id) {
          setIncidentReadiness((previous) => mergeIncidentReadinessMap(previous, event));
          setReadinessLoadState('loaded');
          return;
        }
        // Keep the current rows mounted while live readiness data refreshes.
        // Replacing the list with a loading state made hovered rows flash and
        // could dismiss an in-progress assignment confirmation.
        refreshIncidentReadiness({ background: true });
        refreshStaffingReview({ background: true });
      }
    });
    const unsubscribeDuty = socketService.on('user_duty_status_updated', (event) => {
      const nextStatus = event?.duty_status || event?.status;
      if (!event?.user_id || !nextStatus) return;
      setDuty((previous) => ({
        ...previous,
        [String(event.user_id)]: {
          ...(previous[String(event.user_id)] || {}),
          status: nextStatus,
          responded_at: event.responded_at || null,
        },
      }));
    });
    const refreshForLocation = (event) => {
      const userId = String(event?.user_id || '');
      const isVisibleCandidate = (staffingReviewRef.current?.cards || []).some((card) => (
        (card.roles || []).some((role) => (role.candidates || []).some((candidate) => (
          String(candidate?.user_id || '') === userId
        )))
      ));
      if (!userId || !isVisibleCandidate) return;
      if (locationRefreshTimerRef.current) window.clearTimeout(locationRefreshTimerRef.current);
      locationRefreshTimerRef.current = window.setTimeout(() => {
        locationRefreshTimerRef.current = null;
        refreshIncidentReadiness({ background: true });
      }, 1000);
    };
    const unsubscribeLocation = socketService.on('team_location_updated', refreshForLocation);
    const unsubscribeOffline = socketService.on('team_location_offline', refreshForLocation);
    const unsubscribeReconnect = socketService.on('connect', () => {
      refreshIncidentReadiness({ background: true });
      refreshStaffingReview({ background: true });
    });
    return () => {
      unsubscribeReadiness?.();
      unsubscribeDuty?.();
      unsubscribeLocation?.();
      unsubscribeOffline?.();
      unsubscribeReconnect?.();
      if (locationRefreshTimerRef.current) window.clearTimeout(locationRefreshTimerRef.current);
      locationRefreshTimerRef.current = null;
    };
  }, [open, incident?.id, refreshIncidentReadiness, refreshStaffingReview]);

  const item = React.useMemo(
    () => (allItems || []).find((i) => i?.id === open?.itemId) || open?.itemFallback || null,
    [allItems, open?.itemId, open?.itemFallback]
  );

  // Optimistic assignments for the open card; incoming (possibly stale) props
  // never clobber the selection while a save is in flight.
  React.useEffect(() => {
    if (pendingRef.current > 0 || !item) return;
    const next = item.meta?.roleAssignments || {};
    assignmentsRef.current = next;
    setAssignments(next);
  }, [open?.itemId, item && JSON.stringify(item.meta?.roleAssignments || {})]);

  const dk = open?.variant === 'dark';
  const roles = cleanScenarioRoles(item?.meta?.scenarioRoles) || [];
  const openRole = open?.role || null;
  const activeRole = roles.find((r) => r.role === openRole);
  const assignmentInstructions = getRoleInstructions(activeRole, item?.meta?.assignmentNote);

  const crews = React.useMemo(() => {
    const out = [];
    (teams || []).forEach((team) => {
      (team.crews || []).forEach((c) => {
        if (c?.id && c.is_active !== false) out.push({ id: c.id, crew_name: c.crew_name || 'Crew', team_name: team.team_name || 'Team' });
      });
    });
    return out;
  }, [teams]);

  const idsInOpenRole = React.useMemo(() => {
    const s = new Set();
    ((assignments || {})[openRole] || []).forEach((a) => a?.id && s.add(String(a.id)));
    return s;
  }, [assignments, openRole]);

  const have = uniqueValidAssignmentCount((assignments || {})[openRole]);
  const remaining = activeRole ? Math.max(0, activeRole.count - have) : 0;
  const selectedForRole = ((assignments || {})[openRole] || []);
  const requestedReplacementUserId = open?.replaceUserId ? String(open.replaceUserId) : '';
  const replacementTarget = selectedForRole.find(
    (entry) => String(entry?.id || '') === requestedReplacementUserId,
  ) || null;
  const isReplaceMode = !!replacementTarget;
  const selectionSlots = isReplaceMode ? 1 : remaining;
  const selectedForRoleSignature = JSON.stringify(selectedForRole);
  const activeAvailabilityFilterCount = Object.keys(DEFAULT_PERSONNEL_AVAILABILITY_FILTERS).reduce((count, field) => {
    const current = availabilityFilters[field];
    const initial = DEFAULT_PERSONNEL_AVAILABILITY_FILTERS[field];
    return count + (current === initial || (current == null && initial == null) ? 0 : 1);
  }, 0);

  const resetAvailabilityFilters = () => {
    setAvailabilityFilters({ ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS });
    setActivePreset(null);
  };

  const toggleAvailabilityPreset = (preset) => {
    if (activePreset === preset.id) {
      resetAvailabilityFilters();
      return;
    }
    setAvailabilityFilters({
      ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
      ...preset.filters,
    });
    setActivePreset(preset.id);
  };

  const setAdvancedAvailabilityFilter = (field, nextValue) => {
    setAvailabilityFilters((previous) => ({ ...previous, [field]: nextValue }));
    setActivePreset(null);
  };

  React.useEffect(() => {
    setConfirmReplaceAll(false);
    setBulkNotice('');
  }, [open?.itemId, openRole, selectedForRoleSignature]);
  const otherSameRoleCards = React.useMemo(() => {
    const isPreActivation = item?.plan_state === 'planned'
      || (!!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'requested');
    if (!openRole || !item?.id || !isPreActivation) return [];
    const sourceScenarioId = item?.meta?.plannedFromScenarioId;
    const sourcePlanState = item?.plan_state;
    return (allItems || []).filter((candidate) => (
      isActiveAssignmentCard(candidate)
      && String(candidate.id) !== String(item.id)
      && (!sourceScenarioId || String(candidate?.meta?.plannedFromScenarioId || '') === String(sourceScenarioId))
      && (!sourcePlanState || candidate?.plan_state === sourcePlanState)
      && cleanScenarioRoles(candidate?.meta?.scenarioRoles).some((role) => role.role === openRole)
    ));
  }, [allItems, incident?.is_exercise, incident?.status, item?.id, item?.meta?.plannedFromScenarioId, item?.plan_state, openRole]);
  const otherSameRoleGapCount = otherSameRoleCards.filter((candidate) => {
    const role = cleanScenarioRoles(candidate?.meta?.scenarioRoles).find((row) => row.role === openRole);
    const cap = Math.max(1, Number(role?.count) || 1);
    return uniqueValidAssignmentCount(latestRoleAssignments(candidate)[openRole]) < cap;
  }).length;
  // Real incident and drill staffing can deliberately reuse one person across
  // multiple roles. Only hide someone already selected for this exact role.
  const eligible = (id) => !idsInOpenRole.has(String(id));
  const generalDutyInfoOf = (id) => duty[String(id)] || {};
  const incidentReadinessInfoOf = (id) => incidentReadiness[String(id)] || null;
  const presenceInfoOf = (id) => presenceByUserId?.[String(id)] || (onlineUserIds.has(String(id)) ? { online: true } : null);
  const passesAvailabilityFilter = (id) => {
    if (!passesPersonnelAvailabilityFilters({
      presence: presenceInfoOf(id),
      dutyInfo: generalDutyInfoOf(id),
      readinessInfo: incidentReadinessInfoOf(id),
      readinessLoadState,
      filters: availabilityFilters,
    })) return false;
    return true;
  };

  const assignmentWarningsFor = (id) => {
    return personnelAssignmentWarnings(
      generalDutyInfoOf(id),
      incidentReadinessInfoOf(id),
      readinessLoadState,
    );
  };

  const commit = (nextList) => {
    const cur = assignmentsRef.current;
    const optimistic = { ...cur, [openRole]: nextList };
    assignmentsRef.current = optimistic;
    setAssignments(optimistic);
    pendingRef.current += 1;
    setSaving(true);
    setSaveError('');
    persistRoleChanges(incident, item, allItems, { [openRole]: nextList }, {
      onSettled: ({ succeeded } = {}) => {
        pendingRef.current -= 1;
        if (succeeded === false) setSaveError('Assignment could not be saved. Restoring the server value.');
        if (pendingRef.current === 0) { setSaving(false); onChanged?.(); }
      },
    });
  };

  const addUserNow = (u, via = null) => {
    if (!openRole || !item) return;
    const list = assignmentsRef.current[openRole] || [];
    if (list.some((a) => String(a?.id) === String(u.id))) return;
    const entry = asManualAssignment({ type: 'user', id: String(u.id), name: u.name, ...(via ? { via } : {}) });
    if (isReplaceMode) {
      const targetIndex = list.findIndex(
        (assignment) => String(assignment?.id || '') === requestedReplacementUserId,
      );
      if (targetIndex < 0) {
        setSaveError(`${open?.replaceUserName || 'The current assignee'} changed before the replacement could be saved. Reopen Change and try again.`);
        return;
      }
      const nextList = list.map((assignment, index) => (index === targetIndex ? entry : assignment));
      commit(nextList);
      setOpen((current) => {
        if (!current || String(current.itemId) !== String(item.id) || current.role !== openRole) return current;
        return { ...current, replaceUserId: entry.id, replaceUserName: entry.name };
      });
      return;
    }
    if (activeRole && uniqueValidAssignmentCount(list) >= activeRole.count) return;
    commit([...list, entry]);
  };

  const addGroup = (type, id, name, members) => {
    if (!openRole || !item || !members.length) return;
    if (selectionSlots <= 0) return;
    // Add All must respect the card's required headcount: with more members
    // than open slots, block the bulk add, expand the member list and ask the
    // planner to pick exactly the number needed.
    if (members.length > selectionSlots) {
      const key = `${type}:${id}`;
      setExpandedGroups((p) => ({ ...p, [key]: true }));
      setGroupNotice({
        key,
        msg: isReplaceMode
          ? `Choose one member from ${name} to replace ${replacementTarget?.name || open?.replaceUserName || 'the current assignee'}.`
          : `This role needs only ${remaining} more ${remaining === 1 ? 'person' : 'people'} but ${name} has ${members.length} available — pick ${remaining === 1 ? 'one member' : `${remaining} members`} from the list below.`,
      });
      return;
    }
    setGroupNotice(null);
    const via = { type, id: String(id), name };
    if (isReplaceMode) {
      const member = members[0];
      addUserNow({ id: member.userId, name: member.userName || member.email || 'User' }, via);
      return;
    }
    const list = assignmentsRef.current[openRole] || [];
    const entries = members
      .filter((m) => !list.some((a) => String(a?.id) === String(m.userId)))
      .map((m) => ({ type: 'user', id: String(m.userId), name: m.userName || m.email || 'User', via }));
    if (!entries.length) return;
    commit([...list, ...entries]);
  };

  const requestAddUser = (user, via = null) => {
    const warnings = assignmentWarningsFor(user.id);
    if (warnings.length === 0) {
      addUserNow(user, via);
      return;
    }
    setAssignmentConfirmation({
      kind: 'user',
      title: isReplaceMode
        ? `Replace ${replacementTarget?.name || open?.replaceUserName || 'the current assignee'} with ${user.name || 'this person'} anyway?`
        : `Assign ${user.name || 'this person'} anyway?`,
      people: [user.name || 'User'],
      warnings,
      user,
      via,
    });
  };

  const requestAddGroup = (type, id, name, members) => {
    // Preserve the existing capacity guard before showing any availability
    // confirmation. It expands the group and asks the administrator to pick.
    if (members.length > selectionSlots) {
      addGroup(type, id, name, members);
      return;
    }
    const risky = members.flatMap((member) => {
      const warnings = assignmentWarningsFor(member.userId);
      return warnings.length ? [{ name: member.userName || member.email || 'User', warnings }] : [];
    });
    if (risky.length === 0) {
      addGroup(type, id, name, members);
      return;
    }
    setAssignmentConfirmation({
      kind: 'group',
      title: `Assign ${name} anyway?`,
      people: risky.map((person) => person.name),
      warnings: [...new Set(risky.flatMap((person) => person.warnings))],
      type,
      id,
      name,
      members,
    });
  };

  const confirmPendingAssignment = () => {
    const pending = assignmentConfirmation;
    if (!pending) return;
    setAssignmentConfirmation(null);
    if (pending.kind === 'bulk-fill') {
      fillOtherRoleGaps(true);
      return;
    }
    if (pending.kind === 'group') {
      addGroup(pending.type, pending.id, pending.name, pending.members || []);
      return;
    }
    addUserNow(pending.user, pending.via || null);
  };

  const closeAssignmentConfirmation = () => setAssignmentConfirmation(null);

  const handleAssignmentConfirmationKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeAssignmentConfirmation();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [confirmationCancelRef.current, confirmationConfirmRef.current].filter(Boolean);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const removeUserNow = (userId) => {
    const list = assignmentsRef.current[openRole] || [];
    commit(list.filter((entry) => String(entry?.id) !== String(userId)));
  };

  const persistBulk = (updates, successMessage, emptyMessage) => {
    if (!updates.length) {
      setBulkNotice(emptyMessage);
      setConfirmReplaceAll(false);
      return;
    }
    pendingRef.current += 1;
    setSaving(true);
    setSaveError('');
    setBulkNotice('');
    persistAssignmentUpdates(incident, updates, {
      onSettled: ({ succeeded } = {}) => {
        pendingRef.current -= 1;
        if (succeeded) setBulkNotice(successMessage);
        else setSaveError('Some assignments may not have saved; refreshing server values.');
        if (pendingRef.current === 0) { setSaving(false); onChanged?.(); }
      },
    });
  };

  const sourceReadinessRisks = uniqueManualAssignments(assignmentsRef.current[openRole]).flatMap((entry) => {
    const warnings = assignmentWarningsFor(entry.id);
    return warnings.length ? [{ name: entry.name || 'User', warnings }] : [];
  });

  const fillOtherRoleGaps = (skipReadinessConfirmation = false) => {
    if (!skipReadinessConfirmation && sourceReadinessRisks.length > 0) {
      setAssignmentConfirmation({
        kind: 'bulk-fill',
        title: `Reuse this staffing anyway?`,
        people: sourceReadinessRisks.map((person) => person.name),
        warnings: [...new Set(sourceReadinessRisks.flatMap((person) => person.warnings))],
      });
      return;
    }
    const source = uniqueManualAssignments(assignmentsRef.current[openRole]);
    const updates = [];
    let remainingGapSlots = 0;
    otherSameRoleCards.forEach((target) => {
      const targetRole = cleanScenarioRoles(target?.meta?.scenarioRoles).find((row) => row.role === openRole);
      const cap = Math.max(1, Number(targetRole?.count) || 1);
      const roleAssignments = { ...latestRoleAssignments(target) };
      const existing = Array.isArray(roleAssignments[openRole]) ? roleAssignments[openRole] : [];
      const existingIds = new Set(existing.filter((entry) => entry?.id).map((entry) => String(entry.id)));
      const gap = Math.max(0, cap - existingIds.size);
      if (gap === 0) return;
      const additions = source
        .filter((entry) => entry?.id && !existingIds.has(String(entry.id)))
        .slice(0, gap);
      const next = [...existing, ...additions];
      remainingGapSlots += Math.max(0, cap - uniqueValidAssignmentCount(next));
      if (additions.length === 0) return;
      updates.push({ id: target.id, roleAssignments: { ...roleAssignments, [openRole]: next } });
    });
    persistBulk(
      updates,
      `Added staffing to ${updates.length} other ${openRole} card${updates.length === 1 ? '' : 's'}${remainingGapSlots > 0 ? `; ${remainingGapSlots} slot${remainingGapSlots === 1 ? '' : 's'} still need personnel` : '; all available gaps are filled'}.`,
      `No staffing gaps could be filled on the other ${openRole} cards.`,
    );
  };

  const replaceOtherRoleAssignments = () => {
    const source = uniqueManualAssignments(assignmentsRef.current[openRole]);
    const updates = otherSameRoleCards.flatMap((target) => {
      const targetRole = cleanScenarioRoles(target?.meta?.scenarioRoles).find((row) => row.role === openRole);
      const cap = Math.max(1, Number(targetRole?.count) || 1);
      const roleAssignments = { ...latestRoleAssignments(target) };
      const next = source.slice(0, cap);
      if (JSON.stringify(roleAssignments[openRole] || []) === JSON.stringify(next)) return [];
      return [{ id: target.id, roleAssignments: { ...roleAssignments, [openRole]: next } }];
    });
    setConfirmReplaceAll(false);
    persistBulk(
      updates,
      `Replaced staffing on ${updates.length} other ${openRole} card${updates.length === 1 ? '' : 's'}.`,
      `The other ${openRole} cards already use this staffing.`,
    );
  };

  const groupMembers = React.useCallback((type, id, name) => {
    return expandDistributionRecipients({
      distributionList: [{ type, id, name }],
      appUsers: rawUsers,
      departments,
      teams,
    }).filter((m) => m?.userId);
  }, [rawUsers, departments, teams]);

  const q = query.trim().toLowerCase();
  const rankedSmartCandidates = smartRecommendationCandidates(staffingReview, item?.id, openRole, {
    dutyInfoFor: generalDutyInfoOf,
    readinessInfoFor: incidentReadinessInfoOf,
    readinessLoadState,
  });
  const visibleSmartCandidates = rankedSmartCandidates.filter((candidate) => {
    // Recommended is an explanation of every candidate saved on this exact
    // card, not only the people currently selectable. Keep already-selected or
    // otherwise-assigned people visible and explain their state on the row.
    if (!candidate?.user_id) return false;
    if (!passesAvailabilityFilter(candidate.user_id)) return false;
    if (!q) return true;
    return `${candidate.user_name || ''} ${candidate.email || ''} ${scenarioCandidateSourceSearchText(candidate)}`
      .toLowerCase()
      .includes(q);
  });
  const modalUsers = users
    .filter((u) => eligible(u.id) && passesAvailabilityFilter(u.id) && (!q || (u.name + ' ' + u.position).toLowerCase().includes(q)))
    .sort((a, b) => (
      dutyRank(generalDutyInfoOf(a.id)) - dutyRank(generalDutyInfoOf(b.id))
      || readinessRank(incidentReadinessInfoOf(a.id), readinessLoadState) - readinessRank(incidentReadinessInfoOf(b.id), readinessLoadState)
      || usableReadinessDistance(incidentReadinessInfoOf(a.id)) - usableReadinessDistance(incidentReadinessInfoOf(b.id))
    ));
  // A group matches the search by its own name OR by any member's name/email —
  // searching "41" surfaces the department/crew that 41 belongs to.
  const memberMatches = (all) => all.some((m) => ((m.userName || '') + ' ' + (m.email || '')).toLowerCase().includes(q));
  const modalDepartments = departments
    .map((d) => {
      const name = d.department_name || d.name || 'Department';
      const all = groupMembers('department', d.id, name);
      const members = all.filter((m) => eligible(m.userId) && passesAvailabilityFilter(m.userId));
      return { type: 'department', id: d.id, name, members, all };
    })
    .filter((d) => !q || d.name.toLowerCase().includes(q) || memberMatches(d.all));
  const modalCrews = crews
    .map((c) => {
      const all = groupMembers('crew', c.id, c.crew_name);
      return {
        type: 'crew', id: c.id, name: c.crew_name, sub: c.team_name,
        members: all.filter((m) => eligible(m.userId) && passesAvailabilityFilter(m.userId)),
        all,
      };
    })
    .filter((c) => !q || (c.name + ' ' + c.sub).toLowerCase().includes(q) || memberMatches(c.all));

  if (!open || !activeRole) return null;

  const TABS = [
    { id: 'recommended', label: 'Recommended' },
    { id: 'people', label: 'Users' },
    { id: 'departments', label: 'Departments' },
    { id: 'crews', label: 'Teams/Crews' },
  ];

  const personnelRowMeta = (userId) => {
    const presenceMeta = mobilePresenceMeta(presenceInfoOf(userId));
    const dutyMeta = dutyStatusMeta(generalDutyInfoOf(userId));
    const rawReadinessMeta = readinessStatusMeta(incidentReadinessInfoOf(userId), readinessLoadState);
    const location = readinessLocation(incidentReadinessInfoOf(userId));
    const metadata = [];

    if (!presenceMeta.online) metadata.push(presenceMeta.label);
    if (dutyMeta.status === 'no_response') metadata.push('No duty response');
    else metadata.push(dutyMeta.label);
    if (rawReadinessMeta.status === 'no_response') metadata.push('No incident response');
    else if (rawReadinessMeta.status === 'loading') metadata.push('Checking incident readiness');
    else if (rawReadinessMeta.status === 'unavailable_status') metadata.push('Incident status unavailable');
    metadata.push(location.text);

    let readiness = { label: 'NOT CHECKED', tone: 'muted' };
    if (rawReadinessMeta.status === 'in_duty') readiness = { label: 'READY', tone: 'ready' };
    else if (rawReadinessMeta.status === 'pending') readiness = { label: 'PENDING', tone: 'pending' };
    else if (rawReadinessMeta.status === 'not_in_duty') readiness = { label: 'UNAVAILABLE', tone: 'danger' };

    return {
      metadata: metadata.filter(Boolean).join(' · '),
      readiness,
      presenceLabel: presenceMeta.label,
      dotTone: presenceMeta.online ? 'online' : dutyMeta.status === 'in_duty' ? 'recent' : 'muted',
    };
  };

  const renderPersonRow = ({
    keyValue,
    userId,
    name,
    onAssign,
    dataTestId,
    isTopMatch = false,
    sourceLabel = '',
  }) => {
    const alreadySelected = idsInOpenRole.has(String(userId));
    const selectable = selectionSlots > 0 && !alreadySelected;
    const visual = personnelRowMeta(userId);
    const dotClass = visual.dotTone === 'online'
      ? 'bg-emerald-500'
      : visual.dotTone === 'recent'
        ? 'bg-amber-400'
        : 'bg-slate-400';
    const readinessClass = visual.readiness.tone === 'ready'
      ? (dk ? 'border-emerald-800 bg-emerald-950/70 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
      : visual.readiness.tone === 'pending'
        ? (dk ? 'border-amber-800 bg-amber-950/70 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-700')
        : visual.readiness.tone === 'danger'
          ? (dk ? 'border-rose-800 bg-rose-950/70 text-rose-300' : 'border-rose-200 bg-rose-50 text-rose-700')
          : (dk ? 'border-slate-700 bg-slate-800 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600');
    const displayName = name || 'User';
    const combinedMetadata = [visual.metadata, sourceLabel].filter(Boolean).join(' · ');

    return (
      <button
        key={keyValue || String(userId)}
        type="button"
        disabled={!selectable}
        onClick={onAssign}
        title={sourceLabel || undefined}
        data-testid={dataTestId}
        className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition ${alreadySelected
          ? (dk ? 'border-emerald-800/80 bg-emerald-950/20' : 'border-emerald-200 bg-emerald-50/40')
          : dk
            ? 'border-slate-700 bg-slate-950/35 hover:border-violet-700 hover:bg-violet-950/25'
            : 'border-slate-200 bg-white hover:border-violet-200 hover:bg-violet-50/50'} ${!selectable && !alreadySelected ? 'cursor-not-allowed opacity-45' : ''}`}
      >
        <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-extrabold ${dk ? 'bg-violet-950 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
          {displayName.trim().charAt(0).toUpperCase() || '?'}
          <span
            className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 ${dk ? 'border-slate-900' : 'border-white'} ${dotClass}`}
            title={visual.presenceLabel}
            aria-label={visual.presenceLabel}
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className={`truncate text-xs font-bold ${dk ? 'text-slate-100' : 'text-slate-800'}`}>{displayName}</span>
            {isTopMatch && (
              <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[8px] font-extrabold uppercase tracking-wide ${dk ? 'border-violet-800 bg-violet-950/70 text-violet-300' : 'border-violet-200 bg-violet-50 text-violet-700'}`}>
                <Sparkles className="h-2.5 w-2.5" /> Top match
              </span>
            )}
            <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[8px] font-extrabold uppercase tracking-wide ${readinessClass}`}>
              {visual.readiness.label}
            </span>
          </span>
          <span
            className={`mt-1 block truncate text-[10px] font-medium ${dk ? 'text-slate-400' : 'text-slate-500'}`}
            data-testid={`assign-person-availability-${userId}`}
          >
            {combinedMetadata}
          </span>
        </span>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1.5 text-[10px] font-bold ${alreadySelected
          ? (dk ? 'bg-emerald-700 text-white' : 'bg-emerald-600 text-white')
          : (dk ? 'bg-violet-600 text-white' : 'bg-violet-600 text-white')}`}
        >
          {alreadySelected
            ? '✓ Assigned'
            : isReplaceMode
              ? <>Replace</>
              : <><Plus className="h-3 w-3" /> Assign</>}
        </span>
      </button>
    );
  };

  const renderGroupRow = (g) => {
    const key = `${g.type}:${g.id}`;
    const expanded = !!expandedGroups[key];
    const Icon = g.type === 'department' ? Building2 : Shield;
    return (
      <div key={key} className={`overflow-hidden rounded-lg border ${dk ? 'border-slate-700' : 'border-slate-200'}`}>
        <div className={`flex items-center gap-2 px-3 py-2.5 text-sm ${dk ? 'bg-slate-900 text-slate-200' : 'bg-slate-50 text-slate-800'}`}>
          <button
            type="button"
            data-testid={`assign-group-expand-${g.id}`}
            onClick={() => setExpandedGroups((p) => ({ ...p, [key]: !p[key] }))}
            className={`flex items-center gap-2 flex-1 min-w-0 text-left ${dk ? 'hover:text-white' : 'hover:text-gray-900'}`}
            title={expanded ? 'Hide members' : 'Show members'}
          >
            <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? '' : '-rotate-90'} ${dk ? 'text-slate-500' : 'text-slate-400'}`} />
            <Icon className={`h-4 w-4 shrink-0 ${dk ? 'text-violet-300' : 'text-violet-600'}`} />
            <span className="truncate font-semibold">{g.name}{g.sub ? <span className={dk ? 'text-slate-500' : 'text-slate-400'}> · {g.sub}</span> : null}</span>
          </button>
          <span className={`text-[10px] font-bold shrink-0 ${dk ? 'text-slate-400' : 'text-gray-400'}`}>{g.members.length} selectable</span>
          <button
            type="button"
            data-testid={`assign-group-all-${g.id}`}
            disabled={g.members.length === 0 || selectionSlots === 0}
            onClick={() => requestAddGroup(g.type, g.id, g.sub ? `${g.sub} · ${g.name}` : g.name, g.members)}
            className="shrink-0 rounded-md bg-violet-600 px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wide text-white hover:bg-violet-700 disabled:opacity-40"
          >
            {isReplaceMode ? 'Choose' : 'Add all'}
          </button>
        </div>
        {expanded && (
          <div className={`space-y-1.5 border-t py-2 pl-7 pr-2 ${dk ? 'border-slate-700 bg-slate-950/30' : 'border-slate-200 bg-white'}`}>
            {groupNotice?.key === key && (
              <p className={`mx-1 my-1 rounded-md border px-2 py-1.5 text-[11px] font-medium ${dk ? 'border-amber-700 bg-amber-950/50 text-amber-300' : 'border-amber-300 bg-amber-50 text-amber-700'}`} data-testid={`assign-group-notice-${g.id}`}>
                {groupNotice.msg}
              </p>
            )}
            {g.members.map((m) => renderPersonRow({
              keyValue: `${g.id}:${m.userId}`,
              userId: m.userId,
              name: m.userName || m.email || 'User',
              dataTestId: `assign-member-${g.id}-${m.userId}`,
              onAssign: () => {
                  setGroupNotice(null);
                  requestAddUser(
                    { id: m.userId, name: m.userName || m.email || 'User' },
                    { type: g.type, id: String(g.id), name: g.sub ? `${g.sub} · ${g.name}` : g.name },
                  );
                },
            }))}
            {g.members.length === 0 && (
              <p className={`px-2 py-1.5 text-[11px] ${dk ? 'text-slate-500' : 'text-gray-400'}`}>No selectable members (already assigned or filtered out).</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSmartCandidateRow = (candidate, index) => renderPersonRow({
      keyValue: candidate.user_id,
      userId: candidate.user_id,
      name: candidate.user_name || candidate.email || 'User',
      isTopMatch: index === 0,
      sourceLabel: normalizeScenarioCandidateSources(candidate)
        .filter((source) => source.type !== 'user')
        .map((source) => `${source.type.charAt(0).toUpperCase()}${source.type.slice(1)}: ${source.name}`)
        .join(' · '),
      dataTestId: `assign-recommended-${candidate.user_id}`,
      onAssign: () => requestAddUser(
        { id: candidate.user_id, name: candidate.user_name || candidate.email || 'User' },
        scenarioCandidateVia(candidate),
      ),
  });

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(null)} data-testid={`assign-dropdown-${item?.id}-${openRole}`}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal={assignmentConfirmation ? undefined : 'true'}
        aria-labelledby={modalTitleId}
        aria-hidden={assignmentConfirmation ? 'true' : undefined}
        inert={assignmentConfirmation ? true : undefined}
        tabIndex={-1}
        data-testid="assign-personnel-dialog"
        className={`relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border shadow-2xl ${dk ? 'bg-slate-900 border-slate-700 shadow-black/60' : 'bg-white border-gray-200'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-start justify-between border-b px-5 py-4 ${dk ? 'border-slate-700' : 'border-gray-100'}`}>
          <div className="min-w-0">
            <h3 id={modalTitleId} className={`text-base font-bold ${dk ? 'text-slate-100' : 'text-gray-900'}`}>
              {isReplaceMode ? 'Change assignee' : 'Assign personnel'}
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex max-w-full items-center rounded-full border px-3 py-[3px] text-[13px] font-semibold ${dk ? 'border-violet-700 bg-violet-950/60 text-violet-200' : 'border-violet-200 bg-violet-50 text-violet-700'}`}>
                <span className="truncate">{openRole}</span>
              </span>
              {assignmentInstructions && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={`View assignment instructions for ${openRole}`}
                      data-testid="assign-role-instructions-trigger"
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-[3px] text-[13px] font-semibold transition-colors ${dk
                        ? 'border-violet-800 text-violet-300 hover:bg-violet-950/60'
                        : 'border-violet-200 text-violet-700 hover:bg-violet-50'}`}
                    >
                      <StickyNote className="h-3.5 w-3.5" /> Instructions
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    side="bottom"
                    className={`z-[1200] w-72 p-3 ${dk
                      ? 'border-slate-700 bg-slate-900 text-slate-100'
                      : 'border-violet-200 bg-white text-slate-800'}`}
                    data-testid="assign-role-instructions-content"
                  >
                    <p className={`text-[10px] font-bold uppercase tracking-wider ${dk ? 'text-violet-300' : 'text-violet-700'}`}>
                      Assignment instructions
                    </p>
                    <p className={`mt-1.5 whitespace-pre-wrap text-xs leading-relaxed ${dk ? 'text-slate-300' : 'text-slate-600'}`}>
                      {assignmentInstructions}
                    </p>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <p className={`mt-2 text-[11px] font-semibold ${remaining > 0 ? (dk ? 'text-amber-400' : 'text-amber-600') : (dk ? 'text-emerald-400' : 'text-emerald-600')}`} data-testid="assign-modal-remaining">
              {isReplaceMode
                ? `Replacing ${replacementTarget?.name || open?.replaceUserName || 'current assignee'} · choose one person`
                : remaining > 0
                ? `${have}/${activeRole.count} filled · ${remaining} slot${remaining === 1 ? '' : 's'} left`
                : `${have}/${activeRole.count} filled`}
            </p>
          </div>
          <button type="button" aria-label="Close assign personnel" onClick={() => setOpen(null)} data-testid={`assign-close-${item?.id}`} className={`rounded-md p-1.5 ${dk ? 'text-slate-400 hover:text-slate-100 hover:bg-slate-800' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}>
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Controls */}
        <div className={`space-y-3 border-b px-5 py-3.5 ${dk ? 'border-slate-700' : 'border-gray-100'}`}>
          <div className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 ${dk ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-white'}`}>
            <Search className={`h-4 w-4 ${dk ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === 'recommended'
                ? 'Search recommended people or sources…'
                : tab === 'people'
                  ? 'Search people…'
                  : tab === 'departments'
                    ? 'Search departments…'
                    : 'Search teams or crews…'}
              className={`w-full bg-transparent text-sm outline-none ${dk ? 'text-slate-200 placeholder:text-slate-500' : 'text-slate-800 placeholder:text-slate-400'}`}
              data-testid={`assign-search-${item?.id}`}
            />
          </div>

          <div className={`grid grid-cols-4 gap-1 rounded-lg p-1 ${dk ? 'bg-slate-800' : 'bg-slate-100'}`}>
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`assign-tab-${t.id}-${item?.id}`}
                  className={`inline-flex min-w-0 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[10px] font-bold transition-colors ${tab === t.id
                    ? (dk ? 'bg-violet-600 text-white shadow-sm' : 'bg-white text-violet-700 shadow-sm')
                    : (dk ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700')}`}
                >
                  {t.id === 'recommended' && <Sparkles className="h-3 w-3" />}
                  {t.label}
                </button>
              ))}
          </div>

          <div className="flex flex-wrap items-center gap-1.5" data-testid="assign-smart-presets">
            {PERSONNEL_FILTER_PRESETS.map((preset) => {
              const selected = activePreset === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  aria-pressed={selected}
                  data-testid={`assign-preset-${preset.id}`}
                  onClick={() => toggleAvailabilityPreset(preset)}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[10px] font-bold transition-colors ${selected
                    ? 'border-violet-600 bg-violet-600 text-white'
                    : preset.smart
                      ? (dk ? 'border-violet-700 bg-violet-950/50 text-violet-300 hover:bg-violet-950' : 'border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100')
                      : (dk ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50')}`}
                >
                  {preset.smart && <Sparkles className="h-3 w-3" />}
                  {preset.label}
                </button>
              );
            })}
            <button
              type="button"
              aria-expanded={advancedFiltersOpen}
              onClick={() => setAdvancedFiltersOpen((value) => !value)}
              data-testid="assign-advanced-filters-toggle"
              className={`ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[10px] font-bold ${dk ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
              {activeAvailabilityFilterCount > 0 && (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-violet-600 px-1 text-[8px] font-extrabold text-white">
                  {activeAvailabilityFilterCount}
                </span>
              )}
              <ChevronDown className={`h-3 w-3 transition-transform ${advancedFiltersOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {advancedFiltersOpen && (
            <div
              className={`space-y-2 rounded-lg border p-3 ${dk ? 'border-slate-700 bg-slate-950/35' : 'border-slate-200 bg-slate-50/70'}`}
              data-testid="assign-advanced-filters"
            >
              {ADVANCED_FILTER_GROUPS.map((group) => (
                <div key={group.field} className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className={`w-16 shrink-0 text-[9px] font-extrabold uppercase tracking-wide ${dk ? 'text-slate-400' : 'text-slate-500'}`}>
                    {group.label}
                  </span>
                  {group.options.map((option) => {
                    const current = availabilityFilters[group.field];
                    const selected = option.value == null
                      ? current == null
                      : typeof option.value === 'number'
                        ? Number(current) === option.value
                        : current === option.value;
                    return (
                      <button
                        key={option.value == null ? 'any' : String(option.value)}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setAdvancedAvailabilityFilter(group.field, option.value)}
                        data-testid={`assign-availability-${group.field}-${option.value == null ? 'any' : option.value}`}
                        className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold transition-colors ${selected
                          ? 'border-violet-600 bg-violet-600 text-white'
                          : (dk ? 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-200' : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700')}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              ))}
              <div className={`flex justify-end border-t pt-2 ${dk ? 'border-slate-700' : 'border-slate-200'}`}>
                <button
                  type="button"
                  onClick={resetAvailabilityFilters}
                  data-testid="assign-reset-filters"
                  className={`text-[10px] font-bold underline-offset-2 hover:underline ${dk ? 'text-violet-300' : 'text-violet-700'}`}
                >
                  Reset filters
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Selected people remain removable without reserving empty space when
            the role has no assignment yet. */}
        {selectedForRole.length > 0 && (
          <div
            className={`flex flex-wrap items-center gap-2 border-b px-5 py-2.5 ${dk ? 'border-violet-900 bg-violet-950/30' : 'border-violet-100 bg-violet-50/70'}`}
            data-testid="assign-selected-summary"
          >
            <span className={`text-[9px] font-extrabold uppercase tracking-wider ${dk ? 'text-violet-300' : 'text-violet-700'}`}>
              Selected
            </span>
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {selectedForRole.map((entry) => (
                <span
                  key={entry.id}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${dk ? 'border-violet-800 bg-slate-900 text-slate-200' : 'border-violet-200 bg-white text-slate-700'}`}
                  data-testid={`assign-selected-${entry.id}`}
                >
                  {entry.name || 'User'}
                  <button
                    type="button"
                    onClick={() => removeUserNow(entry.id)}
                    className={dk ? 'text-slate-500 hover:text-rose-400' : 'text-slate-400 hover:text-rose-500'}
                    aria-label={`Remove ${entry.name || 'user'}`}
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <span className={`shrink-0 text-[10px] font-bold ${remaining === 0 ? (dk ? 'text-emerald-400' : 'text-emerald-700') : (dk ? 'text-amber-400' : 'text-amber-700')}`}>
              {have}/{activeRole.count} filled{remaining === 0 ? ' ✓' : ''}
            </span>
          </div>
        )}

        {have > 0 && otherSameRoleCards.length > 0 && (
          <div
            className={`border-b px-4 py-2.5 ${dk ? 'border-slate-700 bg-slate-900' : 'border-gray-100 bg-slate-50'}`}
            data-testid="assign-bulk-actions"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-wide ${dk ? 'text-cyan-400' : 'text-violet-700'}`}>
                  Reuse this staffing
                </p>
                <p className={`mt-0.5 text-[11px] ${dk ? 'text-slate-400' : 'text-slate-500'}`}>
                  Changes above stay on this card. {otherSameRoleCards.length} other active card{otherSameRoleCards.length === 1 ? '' : 's'} require {openRole}.
                </p>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${dk ? 'bg-slate-800 text-slate-300' : 'bg-white text-slate-600 border border-slate-200'}`}>
                {otherSameRoleGapCount} with gaps
              </span>
            </div>

            {!confirmReplaceAll ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => fillOtherRoleGaps()}
                  data-testid="assign-bulk-fill-gaps"
                  className={`rounded-md border px-2.5 py-1.5 text-[10px] font-bold ${dk
                    ? 'border-cyan-800 bg-cyan-950/50 text-cyan-300 hover:bg-cyan-950'
                    : 'border-violet-200 bg-white text-violet-700 hover:bg-violet-50'}`}
                >
                  Fill gaps on other cards
                </button>
                <button
                  type="button"
                  onClick={() => { setConfirmReplaceAll(true); setBulkNotice(''); }}
                  data-testid="assign-bulk-replace"
                  className={`rounded-md border px-2.5 py-1.5 text-[10px] font-bold ${dk
                    ? 'border-amber-800 text-amber-300 hover:bg-amber-950/50'
                    : 'border-amber-300 bg-white text-amber-700 hover:bg-amber-50'}`}
                >
                  Replace staffing on all other cards
                </button>
              </div>
            ) : (
              <div
                className={`mt-2 rounded-md border p-2.5 ${dk ? 'border-amber-800 bg-amber-950/40' : 'border-amber-300 bg-amber-50'}`}
                data-testid="assign-bulk-replace-confirmation"
              >
                <p className={`text-[11px] font-semibold ${dk ? 'text-amber-200' : 'text-amber-800'}`}>
                  Replace the existing {openRole} assignees on all {otherSameRoleCards.length} other cards? This cannot be undone automatically.
                </p>
                {sourceReadinessRisks.length > 0 && (
                  <p className={`mt-1 text-[10px] ${dk ? 'text-amber-300' : 'text-amber-700'}`}>
                    {sourceReadinessRisks.length} selected ${sourceReadinessRisks.length === 1 ? 'person is' : 'people are'} not currently confirmed ready for this incident.
                  </p>
                )}
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={replaceOtherRoleAssignments}
                    data-testid="assign-bulk-replace-confirm"
                    className="rounded-md bg-amber-600 px-2.5 py-1 text-[10px] font-bold text-white hover:bg-amber-700"
                  >
                    Confirm replacement
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmReplaceAll(false)}
                    data-testid="assign-bulk-replace-cancel"
                    className={`rounded-md border px-2.5 py-1 text-[10px] font-bold ${dk ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {bulkNotice && (
              <p className={`mt-2 text-[10px] font-medium ${dk ? 'text-emerald-400' : 'text-emerald-700'}`} data-testid="assign-bulk-notice">
                {bulkNotice}
              </p>
            )}
          </div>
        )}

        {/* Lists */}
        <div className="flex-1 space-y-1 overflow-y-auto p-3">
          {tab === 'recommended' && (
            <div className="space-y-2" data-testid="assign-smart-recommendations">
              <section data-testid="assign-recommendation-explainer" className={`rounded-lg border px-3 py-2.5 ${dk
                ? 'border-violet-900/80 bg-violet-950/25'
                : 'border-violet-200 bg-violet-50/60'}`}
              >
                <div className="flex items-center gap-1.5">
                  <Sparkles className={`h-3.5 w-3.5 ${dk ? 'text-violet-300' : 'text-violet-700'}`} />
                  <p className={`text-[10px] font-extrabold uppercase tracking-wide ${dk ? 'text-violet-300' : 'text-violet-800'}`}>
                    Why these people?
                  </p>
                </div>
                <p className={`mt-1.5 text-[11px] leading-relaxed ${dk ? 'text-slate-400' : 'text-slate-600'}`}>
                  Default Candidates from this Scenario Builder card. Ranked by duty, incident readiness, usable live ETA, then saved order.
                </p>
              </section>

              {staffingReviewLoadState === 'loading' && (
                <p className={`px-2.5 py-4 text-center text-xs ${dk ? 'text-slate-500' : 'text-slate-400'}`}>Loading recommendations…</p>
              )}
              {staffingReviewLoadState === 'unavailable' && (
                <p className={`rounded-lg border px-3 py-3 text-xs ${dk ? 'border-amber-900 bg-amber-950/30 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                  Recommendations could not be loaded. You can still assign from Users, Departments, or Teams/Crews.
                </p>
              )}

              {staffingReviewLoadState === 'loaded' && visibleSmartCandidates.length > 0 && (
                <section className="space-y-1.5" data-testid="assign-ranked-candidates">
                  {visibleSmartCandidates.map(renderSmartCandidateRow)}
                </section>
              )}

              {staffingReviewLoadState === 'loaded' && visibleSmartCandidates.length === 0 && (
                <p className={`px-2.5 py-4 text-center text-xs ${dk ? 'text-slate-500' : 'text-slate-400'}`}>
                  {q
                    ? 'No recommended people or candidate sources match this search.'
                    : 'No default candidates were saved for this role. Use Users, Departments, or Teams/Crews.'}
                </p>
              )}
            </div>
          )}

          {tab === 'people' && (
            <div className="space-y-1.5">
              {modalUsers.map((u) => renderPersonRow({
                keyValue: `u-${u.id}`,
                userId: u.id,
                name: u.name,
                onAssign: () => requestAddUser(u),
                dataTestId: `assign-option-user-${u.id}`,
              }))}
            </div>
          )}
          {tab === 'people' && modalUsers.length === 0 && (
            <p className={`px-2.5 py-3 text-xs ${dk ? 'text-slate-500' : 'text-gray-400'}`}>
              No people match the current availability filters.
            </p>
          )}

          {tab === 'departments' && modalDepartments.map(renderGroupRow)}
          {tab === 'departments' && modalDepartments.length === 0 && (
            <p className={`px-2.5 py-3 text-xs ${dk ? 'text-slate-500' : 'text-gray-400'}`}>No departments found.</p>
          )}

          {tab === 'crews' && modalCrews.map(renderGroupRow)}
          {tab === 'crews' && modalCrews.length === 0 && (
            <p className={`px-2.5 py-3 text-xs ${dk ? 'text-slate-500' : 'text-gray-400'}`}>No teams or crews found.</p>
          )}
        </div>

        {/* Footer */}
        <div className={`border-t px-5 py-3 ${dk ? 'border-slate-700' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between">
            <span className={`text-[11px] ${saveError ? 'text-red-500' : (dk ? 'text-slate-500' : 'text-gray-400')}`}>
              {saveError || (saving ? 'Saving…' : 'Selections save automatically.')}
            </span>
            <button
              type="button"
              onClick={() => setOpen(null)}
              data-testid="assign-modal-done"
              className="rounded-md bg-violet-600 px-4 py-2 text-xs font-bold text-white hover:bg-violet-700"
            >
              Done
            </button>
          </div>
        </div>

      </div>

      {assignmentConfirmation && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-transparent p-4"
          onClick={(event) => event.stopPropagation()}
          data-testid="assign-readiness-confirmation"
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={confirmationTitleId}
            aria-describedby={`${confirmationMessageId} ${confirmationWarningsId}`}
            onKeyDown={handleAssignmentConfirmationKeyDown}
            data-testid="assign-readiness-dialog"
            className={`w-full max-w-sm select-none rounded-xl border-2 p-4 shadow-[0_24px_70px_rgba(15,23,42,0.45)] ring-4 ${dk
              ? 'border-amber-700 bg-slate-900 text-slate-100 ring-amber-950/70'
              : 'border-amber-400 bg-white text-slate-900 ring-amber-100/80'}`}
          >
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${dk ? 'bg-amber-950 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <div>
                  <h4 id={confirmationTitleId} className="text-sm font-bold">{assignmentConfirmation.title}</h4>
                  <p id={confirmationMessageId} className={`mt-1 text-[11px] ${dk ? 'text-slate-400' : 'text-slate-500'}`}>
                    {assignmentConfirmation.people?.length > 1
                      ? `${assignmentConfirmation.people.length} people need confirmation: ${assignmentConfirmation.people.slice(0, 4).join(', ')}${assignmentConfirmation.people.length > 4 ? '…' : ''}`
                      : 'This person is not currently confirmed ready for this assignment.'}
                  </p>
                </div>
              </div>
              <ul id={confirmationWarningsId} className={`mt-3 space-y-1 rounded-lg border px-3 py-2 text-[11px] ${dk ? 'border-slate-700 bg-slate-950/60 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                {assignmentConfirmation.warnings.map((warning) => <li key={warning}>• {warning}</li>)}
              </ul>
              <p className={`mt-2 text-[10px] ${dk ? 'text-slate-500' : 'text-slate-500'}`}>Nothing is saved until you confirm.</p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  ref={confirmationCancelRef}
                  type="button"
                  onClick={closeAssignmentConfirmation}
                  data-testid="assign-readiness-cancel"
                  className={`rounded-md border px-3 py-1.5 text-xs font-bold ${dk ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                >
                  Cancel
                </button>
                <button
                  ref={confirmationConfirmRef}
                  type="button"
                  onClick={confirmPendingAssignment}
                  data-testid="assign-readiness-confirm"
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700"
                >
                  Assign anyway
                </button>
              </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
